import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  ChartingHistorySelectorComponent,
  PerioSettingsService,
} from '@principle-theorem/ng-principle-shared';
import { TypedFormControl } from '@principle-theorem/ng-shared';
import {
  CompareMode,
  IChartHistoryItem,
  IClinicalChart,
} from '@principle-theorem/principle-core/interfaces';
import { getEnumValues, WithRef } from '@principle-theorem/shared';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'pr-perio-compare-chart-selector',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ChartingHistorySelectorComponent,
  ],
  templateUrl: './perio-compare-chart-selector.component.html',
  styleUrl: './perio-compare-chart-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioCompareChartSelectorComponent {
  settings = inject(PerioSettingsService);
  chartHistory$ = new ReplaySubject<IChartHistoryItem[]>(1);
  compareModeCtrl = new TypedFormControl<CompareMode | undefined>(undefined);
  compareMode = getEnumValues(CompareMode);

  @Output()
  selectedChartChange = new EventEmitter<WithRef<IClinicalChart>>();

  @Input()
  set chartHistory(chartHistory: IChartHistoryItem[]) {
    if (chartHistory) {
      this.chartHistory$.next(chartHistory);
    }
  }

  @Input()
  set selectedChart(selectedChart: WithRef<IClinicalChart> | undefined) {
    if (!selectedChart) {
      this.compareModeCtrl.setValue(undefined);
      this.compareModeCtrl.disable();
      return;
    }
    const mode = this.settings.compareMode() ?? CompareMode.Overlay;
    this.compareModeCtrl.setValue(mode);
    this.compareModeCtrl.enable();
  }

  constructor() {
    toObservable(this.settings.compareMode)
      .pipe(takeUntilDestroyed())
      .subscribe((value) =>
        this.compareModeCtrl.setValue(value, { emitEvent: false })
      );

    this.compareModeCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.settings.compareMode.set(value));
  }
}
